import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * Small info icon that opens a popover with a longer explanation.
 * Keeps page headers clean while still making context accessible.
 *
 * Usage:
 *   <HintIcon hint={<>Some <b>rich</b> markup</>} />
 *   <HintIcon hint="Plain string works too" />
 *   <HintIcon hint="…" side="bottom" align="end" size="sm" testId="my-hint" />
 */
export default function HintIcon({
  hint,
  side = "left",
  align = "start",
  size = "md",
  tone = "slate",
  className = "",
  testId,
}) {
  if (!hint) return null;
  const sz = size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const icon = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  const tones = {
    slate: "text-slate-400 hover:text-indigo-600 hover:bg-indigo-50",
    white: "text-white/70 hover:text-white hover:bg-white/10",
    orange: "text-orange-300 hover:text-orange-700 hover:bg-orange-50",
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center justify-center rounded-full transition-colors duration-150 ${sz} ${tones[tone] || tones.slate} ${className}`}
          title="What does this show?"
          aria-label="Show hint"
        >
          <Info className={icon} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-80 text-xs leading-relaxed text-slate-700 border-slate-200 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {hint}
      </PopoverContent>
    </Popover>
  );
}
